import React, { useEffect, useState, useRef } from "react";
import { FaCode, FaJava, FaJs, FaPython, FaReact, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import SEO from "../components/SEO";
import matter from "gray-matter";

// List of Markdown files
const blogFiles = [
  "java-tips.md",
  "string-comparision.md",
  "hashset-duplicate.md",
  "netlify-deploy.md",
  "react-joyride.md",
];

// Category SEO data
const categorySEO = {
  all: {
    title: "All Blogs | CodeRocker",
    description: "Browse all CodeRocker blogs covering Java, React, JavaScript, Python, and more programming topics.",
    keywords: "blogs, programming tutorials, coding tips, java, react, javascript, python",
  },
  java: {
    title: "Java Blogs | CodeRocker",
    description: "Explore Java tutorials, tips, and coding guides to sharpen your Java programming skills.",
    keywords: "java tutorials, java tips, java programming, java developers",
  },
  react: {
    title: "React Blogs | CodeRocker",
    description: "Discover React tutorials, hooks, patterns, and guides to build powerful web apps.",
    keywords: "react tutorials, react hooks, react patterns, frontend development",
  },
  javascript: {
    title: "JavaScript Blogs | CodeRocker",
    description: "Master modern JavaScript with blogs on ES6+, async/await, DOM, and coding best practices.",
    keywords: "javascript tutorials, es6, javascript coding tips, js developers",
  },
  python: {
    title: "Python Blogs | CodeRocker",
    description: "Learn Python programming with tips, automation tricks, and data science tutorials.",
    keywords: "python tutorials, python automation, python tips, data science",
  },
  others: {
    title: "Other Tech Blogs | CodeRocker",
    description: "Read blogs on various technologies, tools, and developer productivity hacks.",
    keywords: "developer tips, coding blogs, technology tutorials",
  },
};

const categorySubtext = {
  all: "Explore all blogs on CodeRocker covering different technologies and programming languages.",
  java: "Deep dive into Java tutorials, performance tips, and practical coding guides for developers.",
  react: "Learn React step-by-step with guides on components, hooks, state management, and patterns.",
  javascript: "Sharpen your JavaScript knowledge with ES6+ features, async patterns, and practical examples.",
  python: "Unlock Python’s power with tutorials on automation, scripting, and data science basics.",
  others: "Discover insights and tutorials on additional tech topics, tools, and productivity tips.",
};

const getCategoryIcon = (category) => {
  switch (category) {
    case "java": return <FaJava className="text-orange-600 text-xl mr-2" />;
    case "javascript": return <FaJs className="text-yellow-500 text-xl mr-2" />;
    case "python": return <FaPython className="text-green-600 text-xl mr-2" />;
    case "react": return <FaReact className="text-cyan-500 text-xl mr-2" />;
    default: return <FaCode className="text-gray-500 text-xl mr-2" />;
  }
};

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 3;
  const tabContainerRef = useRef(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      const results = await Promise.all(
        blogFiles.map(async (file) => {
          const res = await import(`../blogs/${file}`);
          const text = await fetch(res.default).then((r) => r.text());
          const { data, content } = matter(text);

          const lines = text.split("\n").filter((l) => l.trim() !== "");
          const firstLine = lines[0].trim();

          // Extract first .png image from first line
          let firstImage = null;
          const pngMatch = firstLine.match(/!\[.*?\]\((.*?\.png)\)/i);
          if (pngMatch) firstImage = pngMatch[1];

          // Remove all Markdown images from content for description
          let descriptionContent = content.replace(/!\[.*?\]\(.*?\)/g, "").trim();

          // Skip first line if it was a .png image
          if (firstImage) {
            descriptionContent = descriptionContent.split("\n").slice(1).join(" ");
          }

          const description = descriptionContent.substring(0, 150);

          return {
            slug: file.replace(".md", ""),
            title: data.title || file.replace(".md", ""),
            description,
            firstImage,
            content,
            tag: data.tags?.[0] || "others",
            author: data.author || "CodeRocker",
            date: data.date || new Date().toLocaleDateString(),
          };
        })
      );
      setBlogs(results);
    };
    fetchBlogs();
  }, []);

  const categories = ["all", "java", "react", "javascript", "python", "others"];

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = selectedTab === "all" || blog.tag === selectedTab;
    return matchesSearch && matchesTab;
  });

  const totalPages = Math.ceil(filteredBlogs.length / pageSize);
  const paginatedBlogs = filteredBlogs.slice((page - 1) * pageSize, page * pageSize);

  const scrollTabs = (direction) => {
    if (tabContainerRef.current) {
      tabContainerRef.current.scrollBy({
        left: direction === "right" ? 120 : -120,
        behavior: "smooth",
      });
    }
  };

  // Pick SEO data based on selected tab
  const seoData = categorySEO[selectedTab] || categorySEO.all;

  return (
    <div className="flex flex-col items-center w-full">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={`https://coderocker.netlify.app/blogs/${selectedTab}`}
      />

      <h2 className="text-2xl sm:text-3xl font-semibold mb-4 text-center">
        Blogs</h2>

      {/* Subtext under heading */}
      <p className="text-gray-600 dark:text-gray-300 mb-6 text-center max-w-2xl">
        {categorySubtext[selectedTab]}
      </p>

      {/* Search bar */}
      <div className="relative w-full max-w-4xl mb-6">
        <input
          type="text"
          placeholder="Search blogs..."
          className="pl-4 pr-4 py-2 border rounded-full w-full dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center w-full max-w-4xl mb-4">
        <button
          className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-2 sm:hidden"
          onClick={() => scrollTabs("left")}
        >
          <FaChevronLeft />
        </button>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-1" ref={tabContainerRef}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setSelectedTab(cat);
                setPage(1);
              }}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                selectedTab === cat
                  ? "bg-indigo-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700 dark:text-white"
              }`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
        <button
          className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 ml-2 sm:hidden"
          onClick={() => scrollTabs("right")}
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Blog cards */}
      <div className="space-y-4 w-full max-w-4xl">
        {paginatedBlogs.length > 0 ? (
          paginatedBlogs.map((blog) => (
            <div
              key={blog.slug}
              className="border rounded-lg p-6 hover:shadow-lg transition bg-white dark:bg-gray-800"
            >
              {blog.firstImage && (
                <img
                  src={blog.firstImage}
                  alt={blog.title}
                  className="w-full h-48 object-cover rounded mb-4"
                />
              )}
              <div className="flex items-center mb-2">
                {getCategoryIcon(blog.tag)}
                <a
                  href={`/blogs/${blog.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-indigo-700 hover:underline"
                >
                  {blog.title}
                </a>
              </div>
              <p className="text-gray-700 dark:text-gray-300 line-clamp-3 mb-4">{blog.description}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Author: {blog.author} • Published on: {blog.date}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center py-10 text-gray-500 text-lg">No blogs found.</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center flex-wrap gap-2 mt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`px-4 py-2 border rounded ${
                page === i + 1 ? "bg-indigo-500 text-white" : "bg-white dark:bg-gray-800"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BlogSection;
